import { useEffect, useState } from "react";
import { Bell, BellOff, X } from "lucide-react";
import { useStreakReminder } from "@/hooks/useStreakReminder";
import { useAuth } from "@/hooks/useAuth";
import { pushSupported } from "@/lib/push";
import { toast } from "sonner";

const DISMISS_KEY = "inquira.push.optin.dismissed";

function reasonMessage(reason: string) {
  switch (reason) {
    case "unsupported": return "This browser doesn't support web push notifications.";
    case "denied": return "Notifications are blocked in your browser. Enable them in site settings, then reload.";
    case "missing_vapid": return "Push isn't configured on the server (missing VAPID key).";
    case "no_keys": return "Browser didn't return valid push keys — try again in a fresh tab.";
    case "no_user": return "Please sign in first.";
    default: return `Couldn't enable reminders: ${reason}`;
  }
}

/**
 * Inline opt-in banner shown once to signed-in users who haven't decided
 * about notifications yet. Uses the same `useStreakReminder` API as the
 * settings dialog so state stays in sync.
 */
export function PushOptInBanner() {
  const { user } = useAuth();
  const { enableReminders, isEnabled } = useStreakReminder();
  const [dismissed, setDismissed] = useState(true);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">("default");

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setPermission("unsupported");
      return;
    }
    setPermission(Notification.permission);
    setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
  }, []);

  if (!user || isEnabled || dismissed) return null;
  if (permission === "denied" || permission === "unsupported") return null;

  return (
    <div className="mx-auto mt-3 max-w-2xl rounded-2xl border border-neon-blue/30 bg-neon-blue/5 p-3 sm:p-4 flex items-start gap-3">
      <div className="size-9 shrink-0 rounded-full bg-neon-blue/15 flex items-center justify-center">
        <Bell className="size-4 text-neon-blue" />
      </div>
      <div className="flex-1 text-sm">
        <p className="font-semibold text-ink">Turn on study reminders?</p>
        <p className="text-ink-muted mt-0.5">
          We'll send a friendly nudge before you lose your streak. You can turn this off anytime in settings.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={async () => {
              const res = await enableReminders();
              if (res.ok) toast.success("Reminders enabled");
              else toast.error(reasonMessage(res.reason));
              localStorage.setItem(DISMISS_KEY, "1");
              setDismissed(true);
            }}
            className="rounded-lg bg-neon-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
          >
            Enable notifications
          </button>
          <button
            onClick={() => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); }}
            className="rounded-lg border border-rule bg-card px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={() => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); }}
        aria-label="Dismiss"
        className="text-ink-muted hover:text-ink"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

/**
 * Settings row: toggle notifications on/off. Handles unsupported and
 * permission-denied states explicitly so users know why toggling didn't work.
 */
export function NotificationsToggle() {
  const { user } = useAuth();
  const { enableReminders, disableReminders, isEnabled } = useStreakReminder();
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">("default");

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window) || !pushSupported()) {
      setPermission("unsupported");
      return;
    }
    setPermission(Notification.permission);
  }, [isEnabled]);

  if (!user) return null;

  const unsupported = permission === "unsupported";
  const blocked = permission === "denied";

  return (
    <div className="rounded-2xl border border-rule bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink flex items-center gap-2">
            {isEnabled ? <Bell className="size-4 text-neon-blue" /> : <BellOff className="size-4 text-ink-muted" />}
            Study reminders
          </p>
          <p className="mt-1 text-xs text-ink-muted">
            {unsupported
              ? "This browser doesn't support web push notifications."
              : blocked
                ? "Notifications are blocked. Enable them in your browser site settings, then reload."
                : isEnabled
                  ? "You'll get a nudge if you're about to break your streak."
                  : "Get a friendly reminder before midnight to protect your streak."}
          </p>
        </div>
        <button
          disabled={unsupported || blocked}
          onClick={async () => {
            if (isEnabled) {
              disableReminders();
              toast.success("Reminders disabled");
              return;
            }
            const res = await enableReminders();
            if (res.ok) {
              toast.success("Reminders enabled");
              setPermission("granted");
            } else {
              toast.error(reasonMessage(res.reason));
            }
          }}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
            isEnabled ? "bg-neon-blue" : "bg-rule"
          }`}
          aria-pressed={isEnabled}
          aria-label="Toggle study reminders"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              isEnabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>
    </div>
  );
}